import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './index.module.sass';
import dayjs from 'dayjs';
import iconPoints from 'assets/icon_points.svg';
import { ReactComponent as IconQuestion } from 'assets/icon_help_black.svg';
import iconPointEnabled from 'assets/icon_points_enabled.svg';
import iconPointsDisabled from 'assets/icon_points_disabled.svg';
import emptyCoin from 'assets/empty_coin.svg';
import classnames from 'classnames/bind';
import CardItem from 'components/card-item';
import EmptyState from 'components/empty-state';
import amplitude from 'service/amplitude';
import { amountFormat } from 'helpers/currency-helper';
import { useAppContext } from 'use-app';
import UseCoin from './use-coin';
import Spin from 'components/spin';
import iconArrow from 'assets/icon_cad_arrow.svg';
import { PATH } from 'config';
import ButtonDialog from 'components/button-dialog';

const cx = classnames.bind(styles);

const PointsCardList = ({ data, type }) => {
  const history = useHistory();
  const EnabledCard = ({ data }) => {
    return (
      <CardItem
        onClick={data.url ? () => history.push(`${PATH.ORDER_DETAIL}?url=${data.url}`) : () => {}}
        content={
          <div className={styles.content}>
            <div className={styles.name}>{data.title}</div>
            <div className={styles.date}>
              獲得 {dayjs(data.startAt).format('YYYY.MM.DD HH:mm')}
              <br />
              到期 {dayjs(data.endAt).format('YYYY.MM.DD HH:mm')}
            </div>
          </div>
        }
        rightContent={
          <>
            <div className={styles.amount}>
              <img src={iconPointEnabled} alt="enabled" />
              {amountFormat(data.balance)}
            </div>
            <div className={styles.arrow}>{data.url && <img src={iconArrow} alt="arrow" />}</div>
          </>
        }
      />
    );
  };

  const DisabledCard = ({ data }) => {
    return (
      <CardItem
        onClick={data.url ? () => history.push(`${PATH.ORDER_DETAIL}?url=${data.url}`) : () => {}}
        content={
          <div className={styles.content}>
            <div className={styles.name}>{data.title}</div>
            <div className={styles.date}>{dayjs(data.createdAt).format('YYYY.MM.DD HH:mm')}</div>
          </div>
        }
        rightContent={
          <>
            <div className={styles.amount}>
              <img src={iconPointsDisabled} alt="disabled" />
              {data.balance}
            </div>
            <div className={styles.arrow}>{data.url && <img src={iconArrow} alt="arrow" />}</div>
          </>
        }
      />
    );
  };

  if (data.length === 0)
    return type === 'enabled' ? (
      <EmptyState fullText="無可用的行車幣" icon={emptyCoin} />
    ) : (
      <EmptyState fullText="無已使用/過期的行車幣" icon={emptyCoin} />
    );

  return data.map((item, key) => {
    return (
      <div className={styles.card} key={key}>
        {type === 'enabled' ? <EnabledCard data={item} /> : <DisabledCard data={item} />}
      </div>
    );
  });
};

const Coins = () => {
  const { state: appState } = useAppContext();
  const {
    loading,
    coinList,
    loadMoreCoins,
    coinType,
    setCoinType,
    coinPage,
    coinTotalPage,
    ruleDialogOpen,
    setRuleDialogOpen,
    coinRules,
    switchCoins,
    switchLoading,
  } = UseCoin();
  const coinsRef = useRef();
  const coinListRef = useRef();
  const isLoading = useRef(false);

  useEffect(() => {
    amplitude.coins_pageview();
  }, []);

  useEffect(() => {
    if (coinType === 'enabled') {
      amplitude.coins_enabled_pageview();
    } else {
      amplitude.coins_disabled_pageview();
    }
  }, [coinType]);

  useEffect(() => {
    const fetchCoins = () => {
      let readyToFetch = window.innerHeight - coinListRef.current.getBoundingClientRect().bottom > 0;
      if (!isLoading.current && readyToFetch && coinTotalPage !== coinPage) {
        isLoading.current = true;
        loadMoreCoins(coinType, coinPage + 1, () => {
          isLoading.current = false;
        });
      }
    };

    if (coinList.length !== 0) {
      coinsRef.current?.addEventListener('scroll', fetchCoins);
    }
    return () => {
      coinsRef.current?.removeEventListener('scroll', fetchCoins);
    };
  }, [coinPage, coinTotalPage, coinList]); //eslint-disable-line

  const CoinsList = () => {
    if (switchLoading) return <Spin />;
    return (
      <>
        <PointsCardList data={coinList} type={coinType} />
        {loading && <Spin />}
      </>
    );
  };

  const tabList = [
    {
      id: 'enabled',
      text: '獲得',
      amplitudeEvent: amplitude.coins_enabled_onClick,
    },
    {
      id: 'disabled',
      text: '已使用/過期',
      amplitudeEvent: amplitude.coins_disabled_onClick,
    },
  ];

  const topClass = cx({
    top: true,
    'without-expired-points': !appState.me?.mochiCoin?.expiringMochiCoin,
  });

  const barMove = () => {
    if (coinType === 'disabled') {
      if (window.innerWidth >= 768) {
        return 240;
      } else {
        return window.innerWidth / 2;
      }
    }
    return 0;
  };

  return (
    <div className={styles.coins} ref={coinsRef}>
      <ButtonDialog
        isShow={ruleDialogOpen}
        title={coinRules?.action.messageBox.title}
        items={[
          {
            theme: 'positive',
            content: coinRules?.action.messageBox.positiveButton,
            onClick: () => setRuleDialogOpen(false),
          },
        ]}
      >
        {coinRules?.action.messageBox.message.split('\r\n').map((message, key) => (
          <React.Fragment key={key}>
            {message}
            <br />
          </React.Fragment>
        ))}
      </ButtonDialog>

      <div className={topClass}>
        <IconQuestion
          onClick={() => {
            amplitude.coins_help_onClick();
            setRuleDialogOpen(true);
          }}
          className={styles.hint}
        />
        <div className={styles.title}>我的行車幣</div>
        <div className={styles.amount}>
          <img src={iconPoints} alt="points" />
          {amountFormat(appState.me?.mochiCoin?.balance)}
        </div>
        {appState.me?.mochiCoin?.expiringMochiCoin && (
          <div className={styles.desc}>
            將有 {amountFormat(appState.me?.mochiCoin?.expiringMochiCoin.balance)} 點數即將過期
          </div>
        )}
        <div className={styles.tab}>
          <div className={styles.bar} style={{ transform: `translateX(${barMove()}px)` }} />
          {tabList.map(item => {
            const itemClass = cx({
              active: item.id === coinType,
            });
            return (
              <li
                key={item.id}
                onClick={() => {
                  item.amplitudeEvent();
                  setCoinType(item.id);
                  switchCoins(item.id);
                }}
                className={itemClass}
              >
                {item.text}
              </li>
            );
          })}
        </div>
      </div>
      <div className={styles['coins-list']} ref={coinListRef}>
        <CoinsList />
      </div>
    </div>
  );
};

export default Coins;
